/**
 * Shared cricket SSE / initial-state subscription helpers for egress control.
 * Logged-out clients use `tier=lobby` (score + slim scorecard + Match Winner / 410 markets only).
 * Logged-out Trader match play adds `book=catalog` so guests see every market without live prices
 * except Match Winner, plus live cricket chrome (scoregrid / commentary / alerts) for the
 * bat–bowl strip and break panels.
 * Match-league home uses `tier=full` so Squares live balls / scoregrid reach the lobby card.
 */

export const CRICKET_STREAM_TIER_LOBBY = "lobby";
export const CRICKET_STREAM_TIER_FULL = "full";
export const CRICKET_STREAM_BOOK_CATALOG = "catalog";
export const CRICKET_STREAM_PRODUCT_TRADER = "trader";

/** Query suffix for SSE / initial-state (`&tier=…&product=…`). */
export function cricketStreamQueryExtra(options: {
    tier?: string;
    product?: string;
} = {}): string {
    const tier = String(options.tier ?? CRICKET_STREAM_TIER_LOBBY).trim().toLowerCase();
    const product = String(options.product ?? CRICKET_STREAM_PRODUCT_TRADER).trim().toLowerCase();
    const parts: string[] = [];
    if (tier) parts.push(`tier=${encodeURIComponent(tier)}`);
    if (product) parts.push(`product=${encodeURIComponent(product)}`);
    return parts.length ? `&${parts.join("&")}` : "";
}

/** Logged-out lobby visitors poll initial-state on this interval instead of holding SSE open. */
export const CRICKET_LOBBY_POLL_MS = 45_000;

/** Soft client cap for lobby SSE fan-out (server also hard-caps). Live matches should be first. */
export const CRICKET_LOBBY_MAX_MATCH_IDS = 24;

/**
 * HTTP `initial-state` can cover more list cards than SSE. Upcoming matches beyond the SSE cap
 * still need Match Winner prices or lobby cards show SUS while the match page (single-feed) looks fine.
 */
export const CRICKET_LOBBY_SNAPSHOT_MAX_MATCH_IDS = 80;

/** Chunk size when fetching lobby `initial-state` for many matches. */
export const CRICKET_LOBBY_MATCH_ID_BATCH = 20;

export function streamTierForAuth(isLoggedIn: boolean): typeof CRICKET_STREAM_TIER_LOBBY | undefined {
    return isLoggedIn ? undefined : CRICKET_STREAM_TIER_LOBBY;
}

/** Keep first `max` ids (callers should put live / visible matches first). */
export function capMatchIds(ids: string[], max: number = CRICKET_LOBBY_MAX_MATCH_IDS): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of ids) {
        const id = String(raw ?? "").trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
        if (out.length >= max) break;
    }
    return out;
}

export function chunkMatchIds(ids: string[], size: number = CRICKET_LOBBY_MATCH_ID_BATCH): string[][] {
    const list = ids.map((id) => String(id ?? "").trim()).filter(Boolean);
    if (list.length === 0) return [];
    const batches: string[][] = [];
    for (let i = 0; i < list.length; i += size) {
        batches.push(list.slice(i, i + size));
    }
    return batches;
}

/**
 * Fetch lobby/full `initial-state` in batches and merge per-match snapshots.
 * @param queryExtra e.g. `&tier=lobby&product=trader` (must start with `&` or be empty)
 */
export async function fetchStreamInitialStateBatched(
    streamBase: string,
    matchIds: string[],
    queryExtra: string = "",
    signal?: AbortSignal
): Promise<Record<string, unknown>> {
    const base = String(streamBase || "").replace(/\/$/, "");
    if (!base || matchIds.length === 0) return {};
    const extra = queryExtra && !queryExtra.startsWith("&") ? `&${queryExtra}` : queryExtra;
    const merged: Record<string, unknown> = {};
    for (const batch of chunkMatchIds(matchIds)) {
        if (signal?.aborted) break;
        const url = `${base}/stream/initial-state?matchIds=${encodeURIComponent(batch.join(","))}${extra}`;
        try {
            const res = await fetch(url, { method: "GET", credentials: "omit", signal });
            if (!res.ok) continue;
            const state = (await res.json()) as Record<string, unknown>;
            if (state && typeof state === "object") Object.assign(merged, state);
        } catch {
            if (signal?.aborted) break;
        }
    }
    return merged;
}

/** Lobby: event + scores; server omits commentary / scoregrid / lineups / full scorecard. */
export const CRICKET_LOBBY_FEED_FILTER = ["event", "scorecard", "scoreboard"] as const;

/** Trader / Squares lobby stream also needs Match Winner (+ 410 over markets on server for Squares). */
export const CRICKET_LOBBY_WITH_MARKETS_FEED_FILTER = [
    "event",
    "market_update",
    "scorecard",
    "scoreboard",
] as const;

export const CRICKET_TRADER_FULL_FEED_FILTER = [
    "event",
    "market_update",
    "scorecard",
    "scoreboard",
    "commentary",
    "scoregrid",
    "lineups",
    "alerts",
] as const;

export const CRICKET_BULLION_FULL_FEED_FILTER = [
    "event",
    "scorecard",
    "scoreboard",
    "commentary",
    "scoregrid",
    "lineups",
    "alerts",
] as const;

export const SQUARES_FULL_FEED_FILTER = [
    "event",
    "market_update",
    "scorecard",
    "scoreboard",
    "commentary",
    "scoregrid",
    "lineups",
    "alerts",
] as const;

import { SIGNUP_LOGIN_POPUP_MESSAGE } from "../components/shared/loginSignupCopy";

export const LOGIN_GATE_STREAM_MESSAGE = SIGNUP_LOGIN_POPUP_MESSAGE;

type SubscribeToMatchFn = (
    matchId: string | null,
    feedFilter: string[],
    traderTeams?: unknown,
    cb?: (path: string, options: { replace: boolean }) => void,
    product?: string,
    tier?: string,
    book?: string
) => () => void;

/** Subscribe to one match with lobby tier + trimmed feed when the visitor is logged out. */
export function subscribeToMatchForAuth(
    subscribeToMatch: SubscribeToMatchFn,
    matchId: string | null,
    isLoggedIn: boolean,
    fullFeedFilter: readonly string[],
    options: {
        lobbyFeedFilter?: readonly string[];
        product?: string;
        /** Logged-out Trader match play: all markets, live prices only on Match Winner. */
        marketCatalog?: boolean;
    } = {}
): () => void {
    if (!matchId) return () => {};
    const lobbyFilter = options.lobbyFeedFilter ?? CRICKET_LOBBY_FEED_FILTER;
    const feedFilter = isLoggedIn ? [...fullFeedFilter] : [...lobbyFilter];
    const tier = streamTierForAuth(isLoggedIn);
    const product = options.product;
    const book = !isLoggedIn && options.marketCatalog ? CRICKET_STREAM_BOOK_CATALOG : undefined;
    return subscribeToMatch(matchId, feedFilter, undefined, undefined, product, tier, book);
}
